import { useEffect, useMemo, useState } from 'react';
import { Mail, Trash2, Plus, Layers, RefreshCw, Ban, Play } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Skeleton,
  Badge,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useQueryClient } from '@tanstack/react-query';
import {
  useReportRecipients,
  useAddReportRecipient,
  useDeleteReportRecipient,
  useOperationsView,
  useProvisionBindings,
  useJobGroup,
  useBindingAction,
  useOperationsLiveStatus,
  useCdnConfig,
  useUpdateCdnConfig,
  useDeleteCdnConfig,
  type Recipient,
  type OperationsBinding,
  type OperationsCandidate,
  type BindingLiveStatus,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { configTone, configLabel, formatDateTime } from '../shared';
import { brand } from '@/config/brand';

const FREQUENCIES = ['daily', 'weekly', 'monthly'];

const providerLabel = (p: string) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p);

export function OperationsTab({ domain, domainId }: { domain: string; domainId: string }) {
  return (
    <Section title="Operations" description={`Delivery, logging and reporting for ${domain}.`}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <BindingsCard domainId={domainId} />
        <LoggingCard domainId={domainId} />
        <ReportRecipientsCard domainId={domainId} />
      </div>
    </Section>
  );
}

function BindingsCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const qc = useQueryClient();
  const { data: view, isLoading } = useOperationsView(domainId);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const provision = useProvisionBindings(domainId, {
    onSuccess: (r) => {
      trackFeatureAction('cdn', 'binding_provision', 'create');
      if (!r.bindings || r.bindings.length === 0) {
        show({ tone: 'info', title: 'Nothing to provision', description: 'All available providers are already bound.' });
        return;
      }
      setGroupId(r.group_id);
      show({ tone: 'info', title: 'Provisioning started', description: `Creating ${r.bindings.length} binding(s)…` });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not start provisioning', description: errorMessage(e, 'Provisioning failed') }),
  });

  const { data: job } = useJobGroup(groupId);

  // Live vendor status — auto-fetched on mount, self-polls while any binding is
  // still provisioning (no manual refresh).
  const { data: liveRows } = useOperationsLiveStatus(domainId);
  const liveByBinding = new Map((liveRows ?? []).map((r) => [r.binding_id, r]));

  // When the job group finishes, refresh the bindings view and report the outcome.
  useEffect(() => {
    if (!groupId || !job) return;
    const running = job.active > 0;
    if (running) return;
    qc.invalidateQueries({ queryKey: ['cdn', 'domains', domainId, 'operations'] });
    if (job.failed > 0) {
      show({ tone: job.done > 0 ? 'warning' : 'danger', title: 'Provisioning finished with issues', description: `${job.done} succeeded, ${job.failed} failed.` });
    } else {
      show({ tone: 'success', title: 'Bindings provisioned', description: `${job.done} provider(s) synced.` });
    }
    setGroupId(null);
  }, [job, groupId, domainId, qc, show]);

  // Backend hides the surface (404) for non-experimental orgs → render nothing.
  if (!isLoading && !view) return null;

  const busy = provision.isPending || !!groupId;
  const candidates = view?.candidates ?? [];

  return (
    <Card>
      <CardHeader
        actions={
          !isLoading && candidates.length > 0 ? (
            <Button variant="secondary" startIcon={<Plus size={15} />} disabled={busy} onClick={() => setAddOpen(true)}>
              Add provider
            </Button>
          ) : undefined
        }
      >
        <CardTitle>Multi-CDN bindings</CardTitle>
        <CardDescription>Providers this domain is delivered through, and providers it can be added to.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Alert tone="warning">
          Pre-experimental — multi-CDN delivery is an advanced capability. Adding a provider creates the domain on
          that provider and syncs its configuration from the primary (vEdge) binding. Please don't change these
          bindings unless you understand what this does and the impact on your traffic and billing. Contact{' '}
          <a href={`mailto:${brand.emails.support}`}>{brand.emails.support}</a> to learn more.
        </Alert>

        {isLoading ? (
          <Skeleton style={{ height: 96 }} />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {(view?.bindings ?? []).map((b) => {
              const inProgress = !!job?.bindings.find((j) => j.provider_name === b.provider && !['completed', 'failed', 'rolled_back', 'cancelled'].includes(j.status));
              return <BindingRow key={b.binding_id} domainId={domainId} binding={b} inProgress={inProgress} busy={busy} live={liveByBinding.get(b.binding_id)} />;
            })}
          </div>
        )}

        {!isLoading && candidates.length === 0 && (
          <EmptyState size="sm" icon={<Layers size={20} />} title="No providers to add" description="This domain is bound to every configured provider in its family." />
        )}
      </CardBody>

      <AddProviderDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        candidates={candidates}
        busy={busy}
        onActivate={(serviceId) => {
          setAddOpen(false);
          provision.mutate(serviceId);
        }}
      />
    </Card>
  );
}

// AddProviderDialog is the guided "activate another provider" flow: the user
// opens it, reviews the cost terms, picks one provider, and confirms — replacing
// the old inline consent-checkbox + per-row Add buttons. Selection + consent
// reset every time it opens.
function AddProviderDialog({
  open,
  onOpenChange,
  candidates,
  busy,
  onActivate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  candidates: OperationsCandidate[];
  busy: boolean;
  onActivate: (serviceId: string) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset consent each open; preselect when there's only one provider so the
  // common case needs no click.
  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      setSelected(candidates.length === 1 ? candidates[0].service_id : null);
    }
  }, [open, candidates]);

  const chosen = candidates.find((c) => c.service_id === selected);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Add CDN provider</DialogTitle>
          <DialogDescription>
            Activate delivery for this domain on an additional provider. Its configuration is synced from the primary (vEdge) binding.
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Alert tone="warning">
            Enabling an additional CDN (multi-CDN) for this service may incur extra traffic and cost, if any.
          </Alert>

          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>Choose a provider</span>
            {candidates.map((c) => {
              const isSel = c.service_id === selected;
              return (
                <label
                  key={c.service_id}
                  style={{
                    cursor: 'pointer',
                    display: 'grid',
                    gap: 'var(--vn-space-2)',
                    padding: 'var(--vn-space-3)',
                    borderRadius: 'var(--vn-radius-md)',
                    border: `1px solid ${isSel ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                    background: isSel ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                    {/* Radio semantics via a single-select checkbox — clicking a card
                        always selects it (a card is never toggled off). */}
                    <Checkbox checked={isSel} onCheckedChange={() => setSelected(c.service_id)} />
                    <span style={{ flex: 1, fontWeight: 'var(--vn-font-weight-medium)' }}>{providerLabel(c.provider)} CDN</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginRight: 4 }}>Supported:</span>
                    {c.capabilities.supported.map((f) => (
                      <Badge key={f} tone="success" appearance="subtle" size="sm">{f}</Badge>
                    ))}
                  </div>
                  {c.capabilities.unsupported.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginRight: 4 }}>Not supported:</span>
                      {c.capabilities.unsupported.map((f) => (
                        <Badge key={f} tone="neutral" appearance="subtle" size="sm">{f}</Badge>
                      ))}
                    </div>
                  )}
                </label>
              );
            })}
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox checked={acknowledged} onCheckedChange={(v) => setAcknowledged(v === true)} style={{ marginTop: 2 }} />
            <span>I have read and understand that enabling an additional CDN for this service may incur traffic and cost, if any.</span>
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            startIcon={busy ? <RefreshCw size={14} className="vn-spin" /> : <Plus size={14} />}
            disabled={busy || !chosen || !acknowledged}
            onClick={() => chosen && onActivate(chosen.service_id)}
          >
            Activate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// liveTone maps a canonical live status to a StatusPill tone.
const liveTone = (s?: string) =>
  s === 'active' ? 'success' : s === 'provisioning' ? 'warning' : s === 'suspended' ? 'muted' : 'danger';

// operationalLabel maps a binding's live vendor status to an operational label.
const operationalLabel = (live: BindingLiveStatus) =>
  live.on_air
    ? 'Operational'
    : live.live_status === 'provisioning'
      ? 'Provisioning'
      : live.live_status === 'suspended'
        ? 'Suspended'
        : 'Unknown';

// LiveStatusPill shows the real operational status of the binding on its
// provider (auto-polled): Operational (on-air), Provisioning (deploying),
// Suspended, or a per-binding fetch error.
function LiveStatusPill({ live }: { live: BindingLiveStatus }) {
  if (live.error) {
    return (
      <span title={live.error}>
        <StatusPill tone="danger">Operational: error</StatusPill>
      </span>
    );
  }
  // raw_status exposes the vendor's own wording (e.g. EdgeNext "deploying").
  const title = live.raw_status ? `vendor: ${live.raw_status}` : undefined;
  return (
    <span title={title}>
      <StatusPill tone={liveTone(live.live_status)}>{operationalLabel(live)}</StatusPill>
    </span>
  );
}

function BindingRow({ domainId, binding, inProgress, busy, live }: { domainId: string; binding: OperationsBinding; inProgress: boolean; busy: boolean; live?: BindingLiveStatus }) {
  const { show } = useToast();
  const b = binding;

  // Shared toast handlers (plain functions — NOT hooks) so the three
  // useBindingAction calls below stay unconditional and rules-of-hooks-clean.
  const onDone = (verb: 'disable' | 'enable' | 'delete', label: string) => (r: { provider: string; vendor_applied: boolean; note?: string }) => {
    trackFeatureAction('cdn', 'binding_lifecycle', verb);
    show({ tone: r.vendor_applied ? 'success' : 'warning', title: `${providerLabel(r.provider)} ${label}`, description: r.note || undefined });
  };
  const onFail = (verb: string) => (e: unknown) => show({ tone: 'danger', title: `Could not ${verb}`, description: errorMessage(e, `Could not ${verb}`) });

  const disable = useBindingAction(domainId, 'disable', { onSuccess: onDone('disable', 'disabled'), onError: onFail('disable') });
  const enable = useBindingAction(domainId, 'enable', { onSuccess: onDone('enable', 'enabled'), onError: onFail('enable') });
  const remove = useBindingAction(domainId, 'delete', { onSuccess: onDone('delete', 'removed'), onError: onFail('delete') });
  const acting = disable.isPending || enable.isPending || remove.isPending;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-2) var(--vn-space-3)', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)' }}>
      <Layers size={16} color="var(--vn-fg-muted)" />
      <div style={{ flex: 1, display: 'grid', gap: 2, minWidth: 0 }}>
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{providerLabel(b.provider)} CDN</span>
        {(b.uid || live?.cname) && (
          <span
            style={{
              fontSize: 'var(--vn-font-size-xs)',
              color: 'var(--vn-fg-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {[b.uid && `UID: ${b.uid}`, live?.cname && `CNAME: ${live.cname}`].filter(Boolean).join('  ·  ')}
          </span>
        )}
      </div>
      {b.is_core && <Badge tone="accent" appearance="subtle" size="sm">Primary</Badge>}
      {b.drifted && <Badge tone="warning" appearance="subtle" size="sm">Drifted</Badge>}
      <StatusPill tone={inProgress ? 'muted' : b.status === 'active' ? 'success' : b.status === 'disabled' ? 'warning' : 'muted'}>
        {inProgress ? 'Syncing…' : b.status}
      </StatusPill>
      {live && <LiveStatusPill live={live} />}

      {/* Lifecycle actions — secondary (non-core) bindings only. */}
      {!b.is_core && !inProgress && (
        <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
          {b.status === 'active' && (
            <Button variant="ghost" size="sm" startIcon={<Ban size={14} />} disabled={busy || acting} onClick={() => disable.mutate(b.binding_id)}>
              Disable
            </Button>
          )}
          {b.status === 'disabled' && (
            <>
              <Button variant="ghost" size="sm" startIcon={<Play size={14} />} disabled={busy || acting} onClick={() => enable.mutate(b.binding_id)}>
                Enable
              </Button>
              <IconButton variant="ghost" size="sm" aria-label="Remove binding" disabled={busy || acting} onClick={() => remove.mutate(b.binding_id)}>
                <Trash2 size={14} />
              </IconButton>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface LoggingForm {
  access_log_enabled: boolean;
  sampling_rate: string;
}
const LOGGING_DEFAULTS: LoggingForm = { access_log_enabled: true, sampling_rate: '1' };

function hydrateLogging(c: Record<string, unknown>): LoggingForm {
  return {
    access_log_enabled: c.access_log_enabled == null ? true : Boolean(c.access_log_enabled),
    sampling_rate: String(c.sampling_rate ?? 1),
  };
}

// Edge access logging is a real per-domain aspect (GET/PUT /cdn/domains/:id/logging).
// The core (vEdge) binding is the source of truth; the sampling rate keeps 1 request
// in N (clamped 1..10000 to match the backend).
function LoggingCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCdnConfig(domainId, 'logging', {
    vendorMarkers: ['access_log_enabled', 'sampling_rate'],
  });
  const isConfigured = !!cfg;

  const [form, setForm] = useState<LoggingForm>(LOGGING_DEFAULTS);
  const [snapshot, setSnapshot] = useState<LoggingForm>(LOGGING_DEFAULTS);
  useEffect(() => {
    const next = cfg ? hydrateLogging(cfg) : LOGGING_DEFAULTS;
    setForm(next);
    setSnapshot(next);
  }, [cfg]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(snapshot), [form, snapshot]);
  const set = <K extends keyof LoggingForm>(k: K, v: LoggingForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const update = useUpdateCdnConfig(domainId, 'logging', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'logging', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Logging configuration saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save logging configuration', description: errorMessage(e, 'Could not save logging configuration') }),
  });
  const remove = useDeleteCdnConfig(domainId, 'logging', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'logging', 'delete');
      show({ tone: 'success', title: 'Logging configuration removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove logging configuration', description: errorMessage(e, 'Could not remove logging configuration') }),
  });
  const busy = update.isPending || remove.isPending;

  const save = () => {
    const rate = Math.min(10000, Math.max(1, Number(form.sampling_rate) || 1));
    update.mutate({ access_log_enabled: form.access_log_enabled, sampling_rate: rate });
  };

  if (isLoading) return <Skeleton style={{ height: 200 }} />;

  return (
    <Card>
      <CardHeader actions={<StatusPill tone={configTone(isConfigured)}>{configLabel(isConfigured)}</StatusPill>}>
        <CardTitle>Access logs</CardTitle>
        <CardDescription>Edge access logging configuration.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <SettingRow
          title="Access logs"
          description="Whether edge access logs are collected."
          control={<Switch checked={form.access_log_enabled} onCheckedChange={(v) => set('access_log_enabled', v)} />}
        />
        <Field label="Sampling rate" description="1 = every request, 10 = 1 in 10 (max 10,000).">
          {(p) => (
            <Input
              {...p}
              type="number"
              min={1}
              max={10000}
              value={form.sampling_rate}
              disabled={!form.access_log_enabled}
              onChange={(e) => set('sampling_rate', e.target.value)}
              style={{ maxWidth: 160 }}
            />
          )}
        </Field>
        <ActionBar
          sticky={false}
          start={
            isConfigured ? (
              <Button variant="secondary" disabled={busy} onClick={() => remove.mutate()}>
                Remove
              </Button>
            ) : undefined
          }
          end={
            <>
              {isConfigured && (
                <Button variant="secondary" disabled={!dirty || busy} onClick={() => setForm(snapshot)}>
                  Cancel
                </Button>
              )}
              <Button disabled={(isConfigured && !dirty) || busy} onClick={save}>
                {update.isPending ? 'Saving…' : isConfigured ? 'Save changes' : 'Save'}
              </Button>
            </>
          }
        />
      </CardBody>
    </Card>
  );
}

function ReportRecipientsCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: recipients = [], isLoading } = useReportRecipients(domainId);
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [pendingDelete, setPendingDelete] = useState<Recipient | null>(null);

  const add = useAddReportRecipient(domainId, {
    onSuccess: () => { trackFeatureAction('cdn', 'report_recipient', 'create'); show({ tone: 'success', title: 'Recipient added' }); setEmail(''); },
    onError: (e) => show({ tone: 'danger', title: 'Could not add recipient', description: errorMessage(e, 'Could not add recipient') }),
  });
  const remove = useDeleteReportRecipient(domainId, {
    onSuccess: () => { trackFeatureAction('cdn', 'report_recipient', 'delete'); show({ tone: 'success', title: 'Recipient removed' }); setPendingDelete(null); },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove recipient', description: errorMessage(e, 'Could not remove recipient') }),
  });

  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  return (
    <Card>
      <CardHeader>
        <CardTitle>Report recipients</CardTitle>
        <CardDescription>Email addresses that receive periodic CDN reports.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Email address">
            {(p) => <Input {...p} value={email} placeholder="ops@example.com" onChange={(e) => setEmail(e.target.value)} style={{ minWidth: 240 }} />}
          </Field>
          <div style={{ width: 160 }}>
            <Field label="Frequency">
              {(p) => (
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <Button startIcon={<Plus size={15} />} disabled={!validEmail || add.isPending} onClick={() => add.mutate({ email: email.trim(), frequency })}>
            Add
          </Button>
        </div>

        {isLoading ? (
          <Skeleton style={{ height: 80 }} />
        ) : recipients.length === 0 ? (
          <EmptyState size="sm" icon={<Mail size={20} />} title="No recipients yet" description="Add an email to start receiving reports." />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {recipients.map((r) => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-2) var(--vn-space-3)', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)' }}>
                <Mail size={16} color="var(--vn-fg-muted)" />
                <span style={{ flex: 1, fontWeight: 'var(--vn-font-weight-medium)' }}>{r.email}</span>
                <Badge tone="neutral" appearance="subtle" size="sm" style={{ textTransform: 'capitalize' }}>{r.frequency}</Badge>
                <StatusPill tone={r.enabled ? 'success' : 'muted'}>{r.enabled ? 'Active' : 'Inactive'}</StatusPill>
                <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{formatDateTime(r.created_at)}</span>
                <IconButton variant="ghost" size="sm" aria-label="Remove recipient" onClick={() => setPendingDelete(r)}>
                  <Trash2 size={15} />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </CardBody>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Remove recipient?"
        description={`Stop sending reports to ${pendingDelete?.email}?`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Card>
  );
}
